import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { Button } from 'antd';
import ReactTable from 'react-table';
import moment from 'moment';
import 'react-table/react-table.css';
import config from '$config';
import { useMaiaT } from '$app/i18n/useMaiaT';
import Loading from '$app/shared/components/Loading';
import Menu from '../components/Menu';
import GaragePageHeader from '../components/GaragePageHeader';
import { getGarageById } from '../GarageEditPage/actions';
import { listGarageUpdates } from './actions';
import { applicationLabel, garageUpdateLimits } from './limits';

const DivStyled = styled.div`
  padding: 16px;
  
  .ReactTable {
    background-color: #fff;
  }

  .rt-thead {
    z-index: 0;
  }

  .rt-tr-group {
    padding: 8px;
  }
  
  .rt-tr-group:nth-child(odd) {
    background-color: #fbfbfb;
  }
`;

export const GarageUpdatesPage = ({ history, match, limits }) => {
  const garageId = match.params.garageId;
  const t = useMaiaT();
  const pageLimits = limits || garageUpdateLimits(config);
  const [garage, setGarage] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [moreError, setMoreError] = useState(false);

  const loadPage = async (offset, limit) => {
    const page = await listGarageUpdates(garageId, { limit, offset });
    const rows = Array.isArray(page.updates) ? page.updates : [];
    setUpdates((current) => (offset === 0 ? rows : current.concat(rows)));
    setTotal(Number(page.total) || 0);
    return rows;
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    Promise.all([
      getGarageById(garageId)
        .then((garageData) => ({ garage: garageData }))
        .catch((error) => {
          console.error(error);
          return { error };
        }),
      listGarageUpdates(garageId, { limit: pageLimits.initialLimit, offset: 0 })
        .then((page) => ({ page }))
        .catch((error) => {
          console.error(error);
          return { error };
        }),
    ])
      .then(([garageResult, updatesResult]) => {
        if (cancelled) return;
        const garageData = garageResult && !garageResult.error ? garageResult.garage : null;
        setGarage(garageData);
        // No garage here is a failed load, not the create screen.
        if (!garageData || !updatesResult || updatesResult.error) {
          setLoadError(true);
          return;
        }
        const page = updatesResult.page;
        setUpdates(Array.isArray(page.updates) ? page.updates : []);
        setTotal(Number(page.total) || 0);
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [garageId, pageLimits.initialLimit]);

  const allLoaded = !loading && updates.length >= total;
  const showMore = async () => {
    if (allLoaded || loadingMore) return;
    setLoadingMore(true);
    setMoreError(false);
    try {
      await loadPage(updates.length, pageLimits.batchSize);
    } catch (error) {
      console.error(error);
      setMoreError(true);
    } finally {
      setLoadingMore(false);
    }
  };

  const columns = [
    {
      Header: t('maia.garage.updates.dateTime', 'Date and time'),
      id: 'changedAt',
      accessor: 'changedAt',
      Cell: ({ original }) => moment(original.changedAt).format('MMMM Do YYYY, h:mm:ss a'),
    },
    {
      Header: t('maia.garage.updates.author', 'Author'),
      accessor: 'author',
    },
    {
      Header: t('maia.garage.updates.changedField', 'Changed field'),
      accessor: 'changedField',
    },
    {
      Header: t('maia.garage.updates.oldValue', 'Old value'),
      accessor: 'oldValue',
      Cell: ({ value }) => <span style={{ whiteSpace: 'pre-line' }}>{value}</span>,
    },
    {
      Header: t('maia.garage.updates.newValue', 'New value'),
      accessor: 'newValue',
      Cell: ({ value }) => <span style={{ whiteSpace: 'pre-line' }}>{value}</span>,
    },
    {
      Header: t('maia.garage.updates.application', 'Application'),
      id: 'application',
      accessor: 'application',
      Cell: ({ original }) => applicationLabel(original.application, config.segmentGroup, t),
    },
  ];

  if (!garage && loading) return <Loading />;

  return (
    <div>
      {garage && garage.businessName ? (
        <GaragePageHeader history={history} name={garage.businessName} />
      ) : null}
      <Menu selectedItem="updates" garageId={garageId} history={history} />
      <DivStyled>
        {loading ? (
          <Loading />
        ) : (
          <>
            {loadError ? (
              <div>{t('maia.garage.updates.error', 'Updates could not be loaded.')}</div>
            ) : updates.length === 0 ? (
              <div>{t('maia.garage.updates.empty', 'No updates have been recorded for this garage yet.')}</div>
            ) : (
              <ReactTable
                data={updates}
                columns={columns}
                showPagination={false}
                pageSize={updates.length}
              />
            )}
            {moreError ? (
              <div>{t('maia.garage.updates.moreError', 'More updates could not be loaded.')}</div>
            ) : null}
            {!loadError ? (
              <Button
                type="primary"
                style={{ marginTop: 10 }}
                disabled={allLoaded}
                loading={loadingMore}
                onClick={showMore}
              >
                {t('maia.garage.updates.showMore', 'Show more')}
              </Button>
            ) : null}
          </>
        )}
      </DivStyled>
    </div>
  );
};

export default withRouter(GarageUpdatesPage);
